/**
 * Google Analytics 4 - Event Tracking Utility
 * Measurement ID: G-V6GSDZX3QJ
 *
 * Centralised helpers so every part of the app can fire GA4 events
 * without worrying about gtag availability.
 */

// ── Types ────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

type GtagEventParams = Record<string, string | number | boolean | undefined>;

// ── Core helper ──────────────────────────────────────────────────────────────

function trackEvent(eventName: string, params?: GtagEventParams): void {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, params);
    }
  } catch {
    // Silently fail – analytics should never break the app
  }
}

function setUserProperties(properties: GtagEventParams): void {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("set", "user_properties", properties);
    }
  } catch {
    // Silently fail
  }
}

// ── Page Views (SPA route changes) ──────────────────────────────────────────

/**
 * Track a virtual page view in the SPA.
 * Call this on every React Router navigation.
 */
export function trackPageView(path: string, title?: string): void {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", "G-V6GSDZX3QJ", {
        page_path: path,
        page_title: title || document.title,
      });
    }
  } catch {
    // Silently fail
  }
}

// ── Authentication Events ───────────────────────────────────────────────────

export function trackLogin(method: "email" | "google" | "2fa"): void {
  trackEvent("login", { method });
}

export function trackSignup(method: "email" | "google", role?: string): void {
  trackEvent("sign_up", { method, role: role || "CANDIDATE" });
}

export function trackLogout(): void {
  trackEvent("logout");
}

// ── User Identification (for GA4 User-ID feature) ──────────────────────────

/**
 * Set the logged-in user's ID so GA4 can stitch sessions across devices.
 * Only call after successful authentication.
 */
export function identifyUser(userId: string, role?: string): void {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", "G-V6GSDZX3QJ", { user_id: userId });
    }
  } catch {
    // Silently fail
  }
  if (role) {
    setUserProperties({ user_role: role });
  }
}

/**
 * Clear the user ID on logout.
 */
export function clearUserIdentity(): void {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", "G-V6GSDZX3QJ", { user_id: undefined });
    }
  } catch {
    // Silently fail
  }
}

// ── Job Events ──────────────────────────────────────────────────────────────

export function trackJobView(jobId: string, jobTitle?: string, company?: string): void {
  trackEvent("view_item", {
    item_id: jobId,
    item_name: jobTitle,
    item_brand: company,
    item_category: "job",
  });
}

export function trackJobApply(jobId: string, jobTitle?: string, company?: string): void {
  trackEvent("job_apply", {
    job_id: jobId,
    job_title: jobTitle,
    company,
  });
}

export function trackJobPost(jobId?: string, jobTitle?: string): void {
  trackEvent("job_post", {
    job_id: jobId,
    job_title: jobTitle,
  });
}

// ── Search Events ───────────────────────────────────────────────────────────

export function trackSearch(searchTerm: string, resultsCount?: number): void {
  trackEvent("search", {
    search_term: searchTerm,
    results_count: resultsCount,
  });
}

// ── Company / Employer Events ───────────────────────────────────────────────

export function trackCompanyView(companyId: string, companyName?: string): void {
  trackEvent("company_view", {
    company_id: companyId,
    company_name: companyName,
  });
}

// ── Blog / Community Events ─────────────────────────────────────────────────

export function trackBlogView(slug: string, title?: string): void {
  trackEvent("blog_view", { slug, title });
}

export function trackCommunityPost(questionId: string): void {
  trackEvent("community_post", { question_id: questionId });
}

// ── Contact / Conversion Events ─────────────────────────────────────────────

export function trackContactSubmit(): void {
  trackEvent("generate_lead", { source: "contact_form" });
}

// ── Generic custom event ────────────────────────────────────────────────────

export { trackEvent };
